/**
 * AI triage explainability helpers (S5).
 * Assistive rationale only — not diagnostic interpretation.
 */

import type { AITriageCandidate } from "./aiTriageTypes";

export const AI_EXPLANATION_GATE_ID = "AI_EXPLANATION_ACK";

export type ExplanationArtifact = {
  candidateId: string;
  confidence: number;
  sourceSegmentUri: string;
  saliencyUri: string;
  explanationText: string;
  citedEvidenceUris?: string[];
  reviewerAcknowledged: boolean;
  overrideAudit?: {
    reviewerId: string;
    action: "accept" | "reject" | "defer";
    reason: string;
  };
};

export type ExplanationFields = {
  saliencyUri: string;
  explanationText: string;
  sourceSegmentUri: string;
  citedEvidenceUris: string[];
};

function isCitedClinicalEvidence(uri: string): boolean {
  return /^(waveform|report|event|annotation|evidence):\/\//i.test(uri.trim());
}

export function buildHeuristicExplanationFields(input: {
  candidateId: string;
  category: string;
  confidence: number | null;
  channels: string[];
  startTimeSeconds: number;
  endTimeSeconds?: number | null;
  recordingId?: string | null;
  features?: string[];
  existing?: Partial<ExplanationFields> | null;
}): ExplanationFields {
  const end =
    input.endTimeSeconds != null && Number.isFinite(input.endTimeSeconds)
      ? input.endTimeSeconds
      : input.startTimeSeconds + 0.5;
  const segmentFromExisting =
    input.existing?.sourceSegmentUri &&
    isCitedClinicalEvidence(input.existing.sourceSegmentUri)
      ? input.existing.sourceSegmentUri
      : null;
  const sourceSegmentUri =
    segmentFromExisting ||
    (input.recordingId
      ? `waveform://${encodeURIComponent(input.recordingId)}/${input.startTimeSeconds.toFixed(3)}-${end.toFixed(3)}`
      : `evidence://segment/${encodeURIComponent(input.candidateId)}?start=${input.startTimeSeconds}&end=${end}`);

  const saliencyUri =
    (input.existing?.saliencyUri &&
    isCitedClinicalEvidence(input.existing.saliencyUri)
      ? input.existing.saliencyUri
      : null) ||
    `evidence://saliency/${encodeURIComponent(input.candidateId)}?ch=${encodeURIComponent(
      (input.channels[0] || "unknown").toLowerCase(),
    )}&t=${input.startTimeSeconds.toFixed(3)}`;

  const confPct =
    input.confidence == null
      ? "n/a"
      : `${Math.round(Math.max(0, Math.min(1, input.confidence)) * 100)}%`;
  const channelLabel = input.channels.length
    ? input.channels.slice(0, 4).join(", ")
    : "unspecified channels";
  const featureHint = input.features?.length
    ? ` Features considered: ${input.features.slice(0, 5).join(", ")}.`
    : "";
  const heuristicText =
    `Assistive ${input.category} rationale (non-diagnostic): confidence ${confPct} on ${channelLabel} from ${input.startTimeSeconds.toFixed(1)}s–${end.toFixed(1)}s. Confirm or reject on raw EEG before clinical action.${featureHint}`;

  const explanationText =
    input.existing?.explanationText &&
    input.existing.explanationText.trim().length >= 20
      ? input.existing.explanationText.trim()
      : heuristicText;

  const cited = [
    sourceSegmentUri,
    saliencyUri,
    ...(input.existing?.citedEvidenceUris || []),
  ].filter((uri, index, arr) => uri && arr.indexOf(uri) === index);

  return {
    saliencyUri,
    explanationText,
    sourceSegmentUri,
    citedEvidenceUris: cited,
  };
}

export function mergeExplanationIntoPayload(
  payload: Record<string, unknown> | null | undefined,
  fields: ExplanationFields,
): Record<string, unknown> {
  return {
    ...(payload || {}),
    saliencyUri: fields.saliencyUri,
    explanationText: fields.explanationText,
    sourceSegmentUri: fields.sourceSegmentUri,
    citedEvidenceUris: fields.citedEvidenceUris,
  };
}

export function parseExplanationFieldsFromPayload(
  payload: Record<string, unknown> | null | undefined,
): Partial<ExplanationFields> {
  if (!payload || typeof payload !== "object") return {};
  return {
    saliencyUri:
      typeof payload.saliencyUri === "string" ? payload.saliencyUri : undefined,
    explanationText:
      typeof payload.explanationText === "string"
        ? payload.explanationText
        : undefined,
    sourceSegmentUri:
      typeof payload.sourceSegmentUri === "string"
        ? payload.sourceSegmentUri
        : typeof payload.waveformUri === "string"
          ? payload.waveformUri
          : undefined,
    citedEvidenceUris: Array.isArray(payload.citedEvidenceUris)
      ? payload.citedEvidenceUris.filter(
          (uri): uri is string => typeof uri === "string",
        )
      : undefined,
  };
}

function dispositionToOverrideAction(
  status: AITriageCandidate["disposition"]["status"],
): "accept" | "reject" | "defer" | null {
  if (status === "confirmed") return "accept";
  if (status === "rejected" || status === "false_positive") return "reject";
  if (status === "deferred" || status === "escalated") return "defer";
  return null;
}

export function candidateToExplanationArtifact(
  candidate: AITriageCandidate,
): ExplanationArtifact {
  const parsed = parseExplanationFieldsFromPayload(candidate.payload);
  const fields = buildHeuristicExplanationFields({
    candidateId: candidate.candidateId,
    category: candidate.category,
    confidence: candidate.confidence,
    channels: candidate.channels,
    startTimeSeconds: candidate.startTimeSeconds,
    endTimeSeconds: candidate.endTimeSeconds,
    features: candidate.model.inputFeatures,
    existing: parsed,
  });
  const action = dispositionToOverrideAction(candidate.disposition.status);
  const reviewerId = candidate.disposition.reviewerId || null;
  const acknowledged =
    Boolean(reviewerId) &&
    candidate.disposition.status !== "pending" &&
    action != null;

  return {
    candidateId: candidate.candidateId,
    confidence: candidate.confidence ?? 0,
    sourceSegmentUri: fields.sourceSegmentUri,
    saliencyUri: fields.saliencyUri,
    explanationText: fields.explanationText,
    citedEvidenceUris: fields.citedEvidenceUris,
    reviewerAcknowledged: acknowledged,
    overrideAudit:
      acknowledged && reviewerId && action
        ? {
            reviewerId,
            action,
            reason:
              candidate.disposition.notes ||
              candidate.disposition.falsePositiveClass ||
              `disposition:${candidate.disposition.status}`,
          }
        : undefined,
  };
}

export type ExplainabilityGateResult = {
  gate: string;
  ready: boolean;
  warnings: string[];
};

export function evaluateExplainableTriage(
  artifacts: ExplanationArtifact[],
): ExplainabilityGateResult {
  const warnings: string[] = [];
  if (artifacts.length === 0) warnings.push("explanation_artifacts_required");
  for (const artifact of artifacts) {
    if (!artifact.candidateId) warnings.push("candidate_id_required");
    if (!artifact.sourceSegmentUri)
      warnings.push("source_segment_link_required");
    if (!isCitedClinicalEvidence(artifact.sourceSegmentUri))
      warnings.push("waveform_or_report_evidence_required");
    if (!artifact.saliencyUri) warnings.push("saliency_artifact_required");
    if (
      artifact.citedEvidenceUris &&
      artifact.citedEvidenceUris.some((uri) => !isCitedClinicalEvidence(uri))
    ) {
      warnings.push("cited_evidence_uri_scheme_required");
    }
    if (!artifact.explanationText || artifact.explanationText.length < 20)
      warnings.push("explanation_text_required");
    if (!artifact.reviewerAcknowledged)
      warnings.push("reviewer_acknowledgement_required");
    if (!artifact.overrideAudit?.reviewerId || !artifact.overrideAudit.reason)
      warnings.push("override_audit_required");
  }
  return {
    gate: AI_EXPLANATION_GATE_ID,
    ready: warnings.length === 0,
    warnings: [...new Set(warnings)],
  };
}

/** Confirmed candidates that would enter report counts must pass explanation gate when required. */
export function evaluateReportExplainability(
  candidates: AITriageCandidate[],
  options?: { requireExplanation?: boolean },
): ExplainabilityGateResult & { blockingCandidateIds: string[] } {
  if (options?.requireExplanation === false) {
    return {
      gate: AI_EXPLANATION_GATE_ID,
      ready: true,
      warnings: [],
      blockingCandidateIds: [],
    };
  }
  const confirmed = candidates.filter(
    (c) => c.disposition.status === "confirmed",
  );
  if (confirmed.length === 0) {
    return {
      gate: AI_EXPLANATION_GATE_ID,
      ready: true,
      warnings: [],
      blockingCandidateIds: [],
    };
  }
  const artifacts = confirmed.map(candidateToExplanationArtifact);
  const result = evaluateExplainableTriage(artifacts);
  const blockingCandidateIds = confirmed
    .filter((_, index) => {
      const single = evaluateExplainableTriage([artifacts[index]]);
      return !single.ready;
    })
    .map((c) => c.candidateId);
  return { ...result, blockingCandidateIds };
}

export function candidateHasDisplayableExplanation(
  candidate: AITriageCandidate,
): boolean {
  const parsed = parseExplanationFieldsFromPayload(candidate.payload);
  return Boolean(
    (parsed.explanationText && parsed.explanationText.length >= 20) ||
      (parsed.saliencyUri && isCitedClinicalEvidence(parsed.saliencyUri)),
  );
}
